import type { Permission } from '@tumin/domain'
import { useAuthStore } from '@/lib/auth'

/**
 * Requisito de acceso de una pantalla o acción. La interfaz autoriza
 * SIEMPRE por permiso (nunca por nombre de rol), igual que el API: así una
 * institución puede reorganizar sus roles sin cambios de código.
 * - `Permission`: requiere ese permiso.
 * - `{ anyOf }`: basta uno de la lista.
 * - `{ allOf }`: requiere todos.
 * - `undefined`: cualquier usuario autenticado.
 */
export type PermissionRequirement =
  | Permission
  | { anyOf: readonly Permission[] }
  | { allOf: readonly Permission[] }
  | undefined

/** Evalúa un requisito contra un conjunto de permisos. */
export function grants(permissions: readonly Permission[] | null | undefined, requirement: PermissionRequirement): boolean {
  if (requirement === undefined) return true
  const owned = permissions ?? []
  if (typeof requirement === 'string') return owned.includes(requirement)
  if ('anyOf' in requirement) return requirement.anyOf.some((p) => owned.includes(p))
  return requirement.allOf.every((p) => owned.includes(p))
}

const NO_PERMISSIONS: readonly Permission[] = []

/** Permisos efectivos del usuario autenticado. */
export function usePermissions(): readonly Permission[] {
  return useAuthStore((s) => s.employee?.permissions ?? NO_PERMISSIONS)
}

/** `true` si el usuario cumple el requisito (permiso, `anyOf` o `allOf`). */
export function usePermission(requirement: PermissionRequirement): boolean {
  return grants(usePermissions(), requirement)
}
